import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { api, errMsg } from "../lib/api";
import { usePlan } from "./UpgradeGate";
import { useUxMode } from "../lib/uxMode";
import { Sparkles, X, Send, Loader2, Wrench, ChevronRight, ListChecks, CalendarPlus, CheckCircle2, Mic, MicOff } from "lucide-react";

// Global "Ask TotalReach" widget — floats bottom-right on every authed page.
// Suppressed on /ask-totalreach (dedicated page already), auth screens, and public pages.
const HIDDEN_PATHS = [
  "/ask-totalreach", "/login", "/signup", "/request-demo", "/reset-password",
  "/onboarding", "/accept-invite",
];
const HIDDEN_PREFIXES = ["/m/", "/e/", "/race/", "/volunteer/"];

const TOOL_LABEL = {
  get_race_snapshot: "Race",
  get_pending_approvals: "Approvals",
  get_calendar: "Calendar",
  get_fundraising_summary: "Fundraising",
  get_campaigns: "Campaigns",
  get_tasks: "Tasks",
  get_notifications: "Alerts",
  get_qr_scans: "QR",
  create_task: "Task+",
  create_event: "Event+",
  approve_pending: "Approved",
  send_daily_brief_now: "Brief sent",
  mark_notifications_read: "Cleared",
};

const renderMd = (text) => {
  const html = String(text || "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/^#{3,4} (.*)$/gm, "<div class='text-[10px] uppercase tracking-wider text-cyan-300 font-semibold mt-2'>$1</div>")
    .replace(/^#{1,2} (.*)$/gm, "<div class='font-semibold text-white mt-2 text-xs'>$1</div>")
    .replace(/^[-*] (.*)$/gm, "<div class='pl-3 relative before:content-[\"•\"] before:absolute before:left-0 before:text-cyan-300/70'>$1</div>")
    .replace(/\n{2,}/g, "<div class='h-1.5'></div>")
    .replace(/\n/g, "<br/>");
  return <div className="text-[12px] leading-relaxed" dangerouslySetInnerHTML={{ __html: html }} />;
};

const STARTERS = [
  "What needs my attention?",
  "How is our race trending?",
  "How much did we raise this week?",
];

export const AskTotalReachWidget = () => {
  const { pathname } = useLocation();
  const plan = usePlan();
  const uxMode = useUxMode();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sid, setSid] = useState(null);
  const [busy, setBusy] = useState(false);
  const [starters] = useState(STARTERS);
  const [voiceSupported, setVoiceSupported] = useState(false);
  const [listening, setListening] = useState(false);
  const bottomRef = useRef(null);
  const recognitionRef = useRef(null);

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth" });
  }, [messages, busy]);

  // Voice: initialize Web Speech API if the browser supports it
  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    setVoiceSupported(true);
    const rec = new SR();
    rec.lang = "en-US";
    rec.interimResults = true;
    rec.continuous = false;
    rec.onresult = (event) => {
      let transcript = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      setInput(transcript.trim());
    };
    rec.onerror = () => setListening(false);
    rec.onend = () => setListening(false);
    recognitionRef.current = rec;
    return () => { try { rec.abort(); } catch { /* ignore */ } };
  }, []);

  const startVoice = () => {
    if (!recognitionRef.current || listening) return;
    setInput("");
    setListening(true);
    try { recognitionRef.current.start(); } catch { setListening(false); }
  };
  const stopVoice = (autoSend = true) => {
    if (!recognitionRef.current || !listening) return;
    try { recognitionRef.current.stop(); } catch { /* ignore */ }
    setListening(false);
    if (autoSend) {
      // Give the recognition ~250ms to finalize the transcript then send
      setTimeout(() => { setInput((cur) => { if (cur.trim()) send(cur); return ""; }); }, 250);
    }
  };

  const hidden = HIDDEN_PATHS.includes(pathname)
    || HIDDEN_PREFIXES.some(p => pathname.startsWith(p));
  if (hidden) return null;
  if (plan?.gated || plan?.features?.ai === false) return null; // upgrade nudge lives elsewhere

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setInput("");
    setBusy(true);
    setMessages(m => [...m, { role: "user", content: q }]);
    try {
      const { data } = await api.post("/ask-totalreach/ask", { question: q, session_id: sid });
      setSid(data.session_id);
      setMessages(m => [...m,
        { role: "assistant", content: data.reply, tool_calls: data.tool_calls || [], web_sources: data.web_sources || [] }]);
    } catch (e) {
      setMessages(m => [...m,
        { role: "assistant", content: errMsg(e, "Sorry — I hit a snag. Try again.") }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="ask-widget-fab"
          className={`fixed ${uxMode === "simple" ? "bottom-[calc(4.5rem+env(safe-area-inset-bottom))] lg:bottom-6" : "bottom-6"} right-6 z-40 inline-flex items-center gap-2 pl-4 pr-5 py-3 rounded-full bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900 font-semibold text-sm shadow-2xl shadow-cyan-500/30 hover:opacity-90 transition-opacity`}
        >
          <Sparkles className="w-4 h-4" /> Ask TotalReach
        </button>
      )}
      {open && (
        <div
          data-testid="ask-widget-panel"
          className={`fixed ${uxMode === "simple" ? "bottom-[calc(4.5rem+env(safe-area-inset-bottom))] lg:bottom-6" : "bottom-6"} right-6 z-50 w-[400px] max-w-[calc(100vw-2rem)] rounded-2xl border border-white/10 bg-app shadow-2xl flex flex-col overflow-hidden`}
          style={{ height: "560px" }}
        >
          <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between bg-gradient-to-r from-cyan-400/10 to-blue-500/5">
            <div className="text-sm font-display font-semibold flex items-center gap-2 text-white">
              <Sparkles className="w-4 h-4 text-cyan-300" /> Ask TotalReach
              <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-cyan-400/10 border border-cyan-400/30 text-cyan-300">Chief of Staff</span>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="ask-widget-close"
              title="Close Ask TotalReach"
              aria-label="Close Ask TotalReach"
              className="inline-flex items-center gap-1 text-[11px] text-slate-300 hover:text-white bg-white/5 hover:bg-white/10 rounded-full px-2.5 py-1 border border-white/10"
            >
              <X className="w-3 h-3" /> Close
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2.5" data-testid="ask-widget-messages">
            {messages.length === 0 && !busy && (
              <div className="pt-2">
                <div className="text-[12px] text-slate-400 leading-relaxed">
                  I have live access to your race, campaigns, fundraising, approvals and calendar. Try:
                </div>
                <div className="flex flex-col gap-1.5 mt-3">
                  {starters.map((p, i) => (
                    <button
                      key={i}
                      onClick={() => send(p)}
                      data-testid={`ask-widget-starter-${i}`}
                      className="text-[11px] text-left px-3 py-2 rounded-lg border border-white/10 text-slate-300 hover:border-cyan-400/40 hover:bg-cyan-400/5 transition-colors"
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                   data-testid={`ask-widget-msg-${i}`}>
                <div className={`max-w-[92%] rounded-xl px-3 py-2 ${m.role === "user"
                    ? "bg-cyan-400/15 border border-cyan-400/25 text-cyan-50 text-[12px]"
                    : "bg-white/[0.04] border border-white/10 text-slate-200"}`}>
                  {m.role === "user" ? m.content : renderMd(m.content)}
                  {m.role === "assistant" && (m.web_sources || []).length > 0 && (
                    <div className="mt-2 pt-2 border-t border-white/10 flex flex-wrap gap-1" data-testid="ask-widget-sources">
                      {m.web_sources.map((u, k) => (
                        <a key={k} href={u} target="_blank" rel="noreferrer"
                          data-testid={`ask-widget-source-${k}`}
                          className="text-[9px] px-1.5 py-0.5 rounded-full bg-fuchsia-400/10 border border-fuchsia-400/30 text-fuchsia-200 hover:bg-fuchsia-400/20 max-w-[160px] truncate">
                          🌐 {(() => { try { return new URL(u).hostname.replace("www.", ""); } catch { return u; } })()}
                        </a>
                      ))}
                    </div>
                  )}
                  {m.role === "assistant" && (m.tool_calls || []).length > 0 && (
                    <div className="mt-2 pt-2 border-t border-white/10 flex flex-wrap gap-1">
                      {m.tool_calls.map((tc, k) => (
                        <span key={k} className={`inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full ${tc.ok !== false ? "bg-emerald-400/10 border border-emerald-400/30 text-emerald-300" : "bg-rose-400/10 border border-rose-400/30 text-rose-300"}`}>
                          <Wrench className="w-2 h-2" /> {TOOL_LABEL[tc.name] || tc.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start" data-testid="ask-widget-thinking">
                <div className="rounded-xl px-3 py-2 text-[11px] bg-white/[0.04] border border-white/10 text-slate-400 inline-flex items-center gap-2">
                  <Loader2 className="w-3 h-3 animate-spin text-cyan-300" /> Thinking…
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>
          <div className="p-2.5 border-t border-white/10">
            <div className="flex gap-1.5 flex-wrap mb-2" data-testid="ask-widget-quick-actions">
              <button onClick={() => send("Create a task for me — help me title it")}
                      data-testid="ask-widget-action-task"
                      className="text-[10px] px-2.5 py-1 rounded-full border border-white/10 text-slate-400 hover:border-cyan-400/40 hover:text-cyan-300 inline-flex items-center gap-1 transition">
                <ListChecks className="w-3 h-3" /> Task
              </button>
              <button onClick={() => send("Add an event to the calendar — help me pick the title and date")}
                      data-testid="ask-widget-action-event"
                      className="text-[10px] px-2.5 py-1 rounded-full border border-white/10 text-slate-400 hover:border-cyan-400/40 hover:text-cyan-300 inline-flex items-center gap-1 transition">
                <CalendarPlus className="w-3 h-3" /> Event
              </button>
              <button onClick={() => send("Show me pending approvals and help me approve or reject them")}
                      data-testid="ask-widget-action-approve"
                      className="text-[10px] px-2.5 py-1 rounded-full border border-white/10 text-slate-400 hover:border-cyan-400/40 hover:text-cyan-300 inline-flex items-center gap-1 transition">
                <CheckCircle2 className="w-3 h-3" /> Approvals
              </button>
            </div>
            <div className="flex gap-1.5">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder={listening ? "Listening…" : "Ask about anything on this dashboard…"}
                data-testid="ask-widget-input"
                className="flex-1 bg-white/5 border border-white/10 rounded-full px-3.5 py-2 text-xs text-white focus:outline-none focus:border-cyan-400/50 placeholder:text-slate-600"
              />
              {voiceSupported && (
                <button
                  onMouseDown={startVoice}
                  onMouseUp={() => stopVoice(true)}
                  onMouseLeave={() => listening && stopVoice(false)}
                  onTouchStart={(e) => { e.preventDefault(); startVoice(); }}
                  onTouchEnd={(e) => { e.preventDefault(); stopVoice(true); }}
                  data-testid="ask-widget-voice"
                  aria-label={listening ? "Release to send voice message" : "Press and hold to dictate"}
                  className={`w-8 h-8 rounded-full flex items-center justify-center transition ${listening
                    ? "bg-rose-500/20 border border-rose-400/60 text-rose-300 animate-pulse"
                    : "bg-white/5 border border-white/15 text-slate-300 hover:border-cyan-400/40 hover:text-cyan-300"}`}
                >
                  {listening ? <MicOff className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5" />}
                </button>
              )}
              <button
                onClick={() => send()}
                disabled={busy || !input.trim()}
                data-testid="ask-widget-send"
                className="w-8 h-8 rounded-full bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900 flex items-center justify-center disabled:opacity-40"
              >
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              </button>
            </div>
            <a href="/ask-totalreach" className="mt-1.5 text-[10px] text-slate-500 hover:text-cyan-300 inline-flex items-center gap-1"
               data-testid="ask-widget-open-full">
              Open the full assistant <ChevronRight className="w-3 h-3" />
            </a>
          </div>
        </div>
      )}
    </>
  );
};
